import { loadCsvSource } from './csvSource';
import { sourcePlainText } from './sourceText';

const TEXT_EXTENSIONS = new Set(['docx', 'txt', 'csv', 'xlsx']);
const FILE_LIMIT = 15 * 1024 * 1024;
const TEXT_LIMIT = 100000;
const CELL_LIMIT = 50000;
export const sourceExtension = item => ((item.path || item.name || item.title || '').match(/\.([^./]+)$/)?.[1] || '').toLowerCase();
export const isDigitalDocumentSource = item => item.type === 'Attachment' && TEXT_EXTENSIONS.has(sourceExtension(item));

function readable(data, name) {
  const text = data.text.replace(/^\uFEFF/, '').trim();
  if (!text) throw new Error(`${name} has no readable text. Use PDF or images with OCR for scanned content.`);
  if (text.length > TEXT_LIMIT) throw new Error(`${name} is too large for a report. Split it into smaller files.`);
  return { ...data, text };
}

// Reject oversized compressed Office packages before handing them to a parser.
function checkOfficeArchive(buffer, name) {
  const view = new DataView(buffer);
  if (view.byteLength < 4 || view.getUint32(0, true) !== 0x04034b50) {
    throw new Error(`${name} could not be read. Check that it is a valid Office file.`);
  }
  let total = 0;
  let entries = 0;
  for (let offset = 0; offset + 46 <= view.byteLength; offset += 1) {
    if (view.getUint32(offset, true) !== 0x02014b50) continue;
    total += view.getUint32(offset + 24, true);
    entries += 1;
    if (total > 50 * 1024 * 1024 || entries > 2000 || view.getUint32(offset + 24, true) === 0xffffffff) {
      throw new Error(`${name} is too large to extract. Split it into smaller files.`);
    }
    offset += 45 + view.getUint16(offset + 28, true) + view.getUint16(offset + 30, true) + view.getUint16(offset + 32, true);
  }
  if (!entries) throw new Error(`${name} could not be read. Check that it is a valid Office file.`);
}

export async function loadDigitalDocumentSource(item, retrieveFile) {
  const name = item.title || item.name || 'File';
  if (!isDigitalDocumentSource(item)) throw new Error(`${name} is not a supported text document.`);
  if (typeof retrieveFile !== 'function' || !item.path) throw new Error(`Could not load ${name}. Try again.`);
  const extension = sourceExtension(item);
  if (extension === 'csv') {
    const csv = await loadCsvSource(item, retrieveFile, true);
    return readable({ ...csv, kind: 'csv', tables: [{ name, headers: csv.headers, rows: csv.rows }] }, name);
  }
  // Use the existing authenticated storage/decryption reader; no new public URLs.
  const file = await retrieveFile(item.path, item.title || item.name);
  if (!file) throw new Error(`Could not load ${name}. Try again.`);
  if (file.size > FILE_LIMIT) throw new Error(`${name} exceeds the 15 MB file limit.`);
  if (extension === 'txt') return readable({ kind: 'txt', text: await file.text() }, name);
  const buffer = await file.arrayBuffer();
  checkOfficeArchive(buffer, name);
  if (extension === 'docx') {
    const mammoth = (await import('mammoth/mammoth.browser')).default;
    let result;
    try {
      result = await mammoth.convertToHtml({ arrayBuffer: buffer }, {
        convertImage: mammoth.images.imgElement(async () => ({ src: '' })),
      });
    } catch {
      throw new Error(`${name} could not be read. Check that it is a valid DOCX file.`);
    }
    return readable({ kind: 'docx', html: result.value, text: sourcePlainText(result.value) }, name);
  }
  const XLSX = await import('xlsx');
  let workbook;
  try { workbook = XLSX.read(new Uint8Array(buffer), { type: 'array' }); }
  catch { throw new Error(`${name} could not be read. Check that it is a valid XLSX file.`); }
  let cells = 0;
  const tables = workbook.SheetNames.map(sheetName => {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet['!ref']) return { name: sheetName, headers: [], rows: [] };
    const range = XLSX.utils.decode_range(sheet['!ref']);
    cells += (range.e.r - range.s.r + 1) * (range.e.c - range.s.c + 1);
    if (cells > CELL_LIMIT) throw new Error(`${name} has too many cells. Split it into smaller files.`);
    // Formatted/cached values preserve dates and leading zeroes; never execute formulas.
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '', blankrows: false });
    return { name: sheetName, headers: Array.from({ length: Math.max(0, ...rows.map(row => row.length)) }, (_, i) => `Column ${i + 1}`), rows };
  }).filter(table => table.rows.some(row => row.some(cell => String(cell).trim())));
  const text = tables.map(table => `Sheet: ${table.name}\n` + table.rows.map((row, index) => `Row ${index + 1}\n` + row.map((cell, column) => `Column ${column + 1}: ${cell}`).join('\n')).join('\n\n')).join('\n\n');
  return readable({ kind: 'xlsx', tables, text }, name);
}

const REPORT_FILE_EXTENSIONS = new Set(['docx', 'pdf', 'jpg', 'jpeg', 'png', 'webp', 'gif']);
export async function prepareReportSourceAttachments(items, retrieveFile, { preparedWord = false } = {}) {
  const refs = [], skipped = [], textParts = [];
  for (const item of items || []) {
    if (isDigitalDocumentSource(item) && !(preparedWord && sourceExtension(item) === 'docx')) {
      const data = await loadDigitalDocumentSource(item, retrieveFile);
      textParts.push(`Source: ${item.title || item.name}\n${data.text}`);
    } else if (item.path && REPORT_FILE_EXTENSIONS.has(sourceExtension(item))) refs.push(item.path);
    else skipped.push(item.name || item.title || 'attachment');
  }
  return { refs, skipped, text: textParts.join('\n\n') };
}
